export const ActionSetAttributes = {
    CASE: "Case",
};

export const AnimationElementAttributes = {
    /*
     * Alpha (transparency) for this display object. 0 is fully transparent, 1 is fully opaque.
     * @property Alpha
     * @type Number
     * @default 1
     */
    ALPHA: "Alpha",
    ALPHA_TWEEN: "AlphaTween",
    ALPHA_TWEEN_EASE: "AlphaTweenEase",
    ALPHA_TWEEN_DELAY: "AlphaTweenDelay",
    ALPHA_TWEEN_DURATION: "AlphaTweenDuration",

    ANCHOR: "Anchor",
    /*
     * A ImageElement instance that defines a mask for this display object.
     * The shape's transformation will be applied relative to the display object's parent coordinates (as if it were a child of the parent).
     * @property BitmapMask
     * @type string
     * @default null
     */
    BITMAP_MASK: "BitmapMask",
    BITMAP_MASK_SHOW: "BitmapMaskShow",
    BITMAP_MASK_X: "BitmapMaskX",
    BITMAP_MASK_Y: "BitmapMaskY",
    DEBUG_INFO: "DebugInfo",
    DEBUG: "Debug",
    /*
     * A ShapeElement instance that defines a vector mask (clipping path) for this display object.
     * The shape's transformation will be applied relative to the display object's parent coordinates (as if it were a child of the parent).
     * @property Mask
     * @type ShapeElement
     * @default null
     */
    MASK: "Mask",
    /*
     * X pivot for container. Used mainly for scale tweening and rotation.
     * @property PivotX
     * @type number
     * @default null
     */
    PIVOT_X: "PivotX",
    /*
     * Y pivot for container. Used mainly for scale tweening and rotation.
     * @property PivotY
     * @type number
     * @default null
     */
    PIVOT_Y: "PivotY",
    /*
     * Start playing animation.
     * @property Play
     * @type boolean
     */
    PLAY: "Play",
    POSITION_TWEEN_X: "PositionTweenX",
    POSITION_TWEEN_Y: "PositionTweenY",
    POSITION_TWEEN_EASE: "PositionTweenEase",
    POSITION_TWEEN_DURATION: "PositionTweenDuration",
    POSITION_TWEEN_POSITION: "PositionTweenPosition",
    POSITION_TWEEN_POSITION_VALUE: {
        ABSOLUTE: "absolute",
        RELATIVE: "relative",
    },
    /*
     *
     */
    RANDOM_START_FRAME: "RandomStartFrame",
    /*
     * Indicates if animation should animate reverse.
     * @property Reverse
     * @type boolean
     * @default false
     */
    REVERSE: "Reverse",
    /*
     * Rotation in degrees for this display object.
     * @property Rotation
     * @type Number
     * @default 0
     */
    ROTATION: "Rotation",
    /*
     * Tween rotation of this display object with children.
     * For duration use RotateTweenDuration (number - frames).
     * For tweenEase use RotateTweenEase (string - name of easeing function).
     * @property RotateTweenDegrees
     * @type Array<numbers> - consist of two values (start degrees; end degrees) or three values (start degrees, middle degrees, end degrees)
     * @default null
     */
    ROTATION_TWEEN: "RotationTween",
    ROTATION_TWEEN_EASE: "RotationTweenEase",
    ROTATION_TWEEN_DURATION: "RotationTweenDuration",
    ROTATION_TWEEN_DELAY: "RotationTweenDelay",
    /*
     * Tween scale of this display object with children.
     * For ease use ScaleTweenEase (string - name of easeing function).
     * For scaling X use ScaleTweenX (consist of two values (start scaleX; end scaleX) or three values (start scaleX; middle scaleX, end scaleX)).
     * For scaling Y use ScaleTweenY (consist of two values (start scaleY; end scaleY) or three values (start scaleY; middle scaleY, end scaleY)).
     * @property ScaleTweenDuration
     * @type number - frames
     * @default null
     */
    SCALE_TWEEN: "ScaleTween",
    SCALE_TWEEN_EASE: "ScaleTweenEase",
    SCALE_TWEEN_DURATION: "ScaleTweenDuration",
    /*
     * The factor to stretch this display object horizontally.
     * For example, setting scaleX to 2 will stretch the display object to twice its nominal width. To horizontally flip an object, set the scale to a negative number.
     * @property ScaleX
     * @type Number
     * @default 1
     */
    SCALE_X: "ScaleX",
    /*
     * The factor to stretch this display object vertically.
     * For example, setting scaleY to 0.5 will stretch the display object to half its nominal height. To vertically flip an object, set the scale to a negative number.
     * @property ScaleY
     * @type Number
     * @default 1
     */
    SCALE_Y: "ScaleY",
    /*
     * The factor to stretch this display object horizontally and vertically. Use this instead of using ScaleX and ScaleY with same value.
     * @property Scale
     * @type Number
     * @default 1
     */
    SCALE: "Scale",
    /*
     * Random rotation in degrees for this display object.
     * @property StartRotation
     * @type Number
     * @default null
     */
    START_ROTATION: "StartRotation",
    STYLE: "Style",
    STYLE2: "Style2",
    /*
     * Tint of animation. Hex color which colorize movie clip.
     * @property Tint
     * @type string
     * @default null
     */
    TINT: "Tint",
    /*
     * Indicates whether this display object should be rendered to the canvas and included when running the Stage Stage/getObjectsUnderPoint method.
     * @property Visible
     * @type Boolean
     * @default true
     */
    VISIBLE: "Visible",
    /*
     * X (horizontal) position of the display object, relative to its parent.
     * @property X
     * @type Number
     * @default 0
     */
    X: "X",
    /*
     * Y (vertical) position of the display object, relative to its parent.
     * @property Y
     * @type Number
     * @default 0
     */
    Y: "Y",
};

/**
 *
 * @type {{ANIMATION: string; START_FRAME: string; END_FRAME: string; LOOP: string; STOP: string; SPEED: string; ALPHA: string; TINT: string; BLEND_MODE: string}}
 */
export const AnimationStyleAttributes = {
    /*
     * Animation is library ID from javascript libs.
     * @property Animation
     * @type string
     * @default null
     */
    ANIMATION: "Animation",
    /*
     * Frames is a semicolon separated list of individual frames used for the animation
     * @type string
     * @default null
     */
    FRAMES: "Frames",
    /*
     * Start frame is number of frame when animation start on timeline.
     * @property StartFrame
     * @type number
     * @default 0
     */
    START_FRAME: "StartFrame",
    /*
     * End frame is number of frame when animation ends on timeline.
     * Use only if end frame is smaller then last frame on timeline.
     * @property EndFrame
     * @type number
     * @default null
     */
    END_FRAME: "EndFrame",
    /*
     * Indicates whenever this MovieClip should loop when it reaches the end of its timeline.
     * @property Loop
     * @type boolean
     * @default false
     */
    LOOP: "Loop",
    /*
     * Indicates whether this MovieClip shoud play only single frame animation.
     * @property Stop
     * @type boolean
     * @default false
     */
    STOP: "Stop",
    /*
     * Speed of animation. Less than one will slow down animation. Calc with game FPS.
     * @property Speed
     * @type number
     * @default 1
     */
    SPEED: "Speed",
    /*
     * Alpha (transparency) for this display object. 0 is fully transparent, 1 is fully opaque.
     * @property Alpha
     * @type Number
     * @default 1
     */
    ALPHA: "Alpha",
    /*
     * Tint of animation. Hex color which colorize movie clip.
     * @property Tint
     * @type string
     * @default null
     */
    TINT: "Tint",
    /*
     * Blend mode for animation.
     * Bleding modes:
     *   0 = NORMAL;
     *   1 = ADD;
     *   2 = MULTIPLY;
     *   3 = SCREEN;
     *   4 = OVERLAY;
     *   5 = DARKEN;
     *   6 = LIGHTEN;
     *   7 = COLOR_DODGE;
     *   8 = COLOR_BURN;
     *   9 = HARD_LIGHT;
     *   10 = SOFT_LIGHT;
     *   11 = DIFFERENCE;
     *   12 = EXCLUSION;
     *   13 = HUE;
     *   14 = SATURATION;
     *   15 = COLOR;
     *   16 = LUMINOSITY;
     * @property BlendMode
     * @type number
     * @default null
     */
    BLEND_MODE: "BlendMode",
    /*
     * Indicates if animation should animate reverse.
     * @property Reverse
     * @type boolean
     * @default false
     */
    REVERSE: "Reverse",
};

export const BaseElementAttributes = {
    /*
     * Alpha (transparency) for this display object. 0 is fully transparent, 1 is fully opaque.
     * @property Alpha
     * @type Number
     * @default 1
     */
    ALPHA: "Alpha",

    ALPHA_TWEEN: "AlphaTween",
    ALPHA_TWEEN_DELAY: "AlphaTweenDelay",
    ALPHA_TWEEN_DURATION: "AlphaTweenDuration",
    ALPHA_TWEEN_EASE: "AlphaTweenEase",
    ALPHA_TWEEN_LOOP: "AlphaTweenLoop",
    /*
     * A ImageElement instance that defines a mask for this display object.
     * The shape's transformation will be applied relative to the display object's parent coordinates (as if it were a child of the parent).
     * @property BitmapMask
     * @type string
     * @default null
     */
    BITMAP_MASK: "BitmapMask",
    BITMAP_MASK_SHOW: "BitmapMaskShow",
    BITMAP_MASK_X: "BitmapMaskX",
    BITMAP_MASK_Y: "BitmapMaskY",
    /*
     * A ShapeElement instance that defines a vector mask (clipping path) for this display object.
     * The shape's transformation will be applied relative to the display object's parent coordinates (as if it were a child of the parent).
     * @property Mask
     * @type ShapeElement
     * @default null
     */
    MASK: "Mask",
    /*
     * X pivot for container. Used mainly for scale tweening and rotation.
     * @property PivotX
     * @type number
     * @default null
     */
    PIVOT_X: "PivotX",
    /*
     * Y pivot for container. Used mainly for scale tweening and rotation.
     * @property PivotY
     * @type number
     * @default null
     */
    PIVOT_Y: "PivotY",
    POSITION_TWEEN_POSITION: "ChildsPositionTweenPosition",
    POSITION_TWEEN_X: "ChildsPositionTweenX",
    POSITION_TWEEN_Y: "ChildsPositionTweenY",
    POSITION_TWEEN_EASE: "ChildsPositionTweenEase",
    POSITION_TWEEN_DELAY: "ChildsPositionTweenDelay",
    POSITION_TWEEN_DURATION: "ChildsPositionTweenDuration",
    POSITION_TWEEN_POSITION_VALUE: {
        ABSOLUTE: "absolute",
        RELATIVE: "relative",
    },
    /*
     *
     */
    ROTATION_TWEEN: "RotationTween",
    /**
     * @deprecated Use ROTATION_TWEEN instead ROTATION_TWEEN_DEGREES
     */
    ROTATION_TWEEN_DEGREES: "RotationTweenDegrees",
    ROTATION_TWEEN_DELAY: "RotationTweenDelay",
    ROTATION_TWEEN_DURATION: "RotationTweenDuration",
    ROTATION_TWEEN_EASE: "RotationTweenEase",
    /*
     * Rotation in degrees for this display object.
     * @property Rotation
     * @type Number
     * @default 0
     */
    ROTATION: "Rotation",
    SCALE_TWEEN: "ScaleTween",
    SCALE_TWEEN_DELAY: "ScaleTweenDelay",
    /**
     * @deprecated Use SCALE_TWEEN instead SCALE_TWEEN_DURATION
     */
    SCALE_TWEEN_DURATION: "ScaleTweenDuration",
    SCALE_TWEEN_EASE: "ScaleTweenEase",
    SCALE_TWEEN_X: "ScaleTweenX",
    SCALE_TWEEN_Y: "ScaleTweenY",
    /*
     * The factor to stretch this display object horizontally.
     * For example, setting scaleX to 2 will stretch the display object to twice its nominal width. To horizontally flip an object, set the scale to a negative number.
     * @property ScaleX
     * @type Number
     * @default 1
     */
    /*
     * The factor to stretch this display object horizontally and vertically. Use this instead of using ScaleX and ScaleY with same value.
     * @property Scale
     * @type Number
     * @default 1
     */
    SCALE: "Scale",
    SCALE_X: "ScaleX",
    /*
     * The factor to stretch this display object vertically.
     * For example, setting scaleY to 0.5 will stretch the display object to half its nominal height. To vertically flip an object, set the scale to a negative number.
     * @property ScaleY
     * @type Number
     * @default 1
     */
    SCALE_Y: "ScaleY",
    /*
     * Random rotation in degrees for this display object.
     * @property StartRotation
     * @type Number
     * @default null
     */
    START_ROTATION: "StartRotation",
    TWEEN_ENGINE: "TweenEngine",
    /*
     * Indicates whether this display object should be rendered to the canvas and included when running the Stage Stage/getObjectsUnderPoint method.
     * @property Visible
     * @type Boolean
     * @default true
     */
    VISIBLE: "Visible",
    /*
     * X (horizontal) position of the display object, relative to its parent.
     * @property X
     * @type Number
     * @default 0
     */
    X: "X",
    /*
     * Y (vertical) position of the display object, relative to its parent.
     * @property Y
     * @type Number
     * @default 0
     */
    Y: "Y",
};

export const ButtonElementAttributes = {
    /*
     * The X (horizontal) position of the shape, relative to its parent.
     * @property X
     * @type Number
     * @default 0
     */
    X: "X",
    /*
     * The Y (vertical) position of the shape, relative to its parent.
     * @property Y
     * @type Number
     * @default 0
     */
    Y: "Y",

    /*
     * Shape's width.
     * @property Width
     * @type number
     * @default null
     */
    WIDTH: "Width",
    /*
     * Shape's height.
     * @property Height
     * @type number
     * @default null
     */
    HEIGHT: "Height",

    ACTION: "Action",

    EVENT: "Event",

    ACTION_SET: "ActionSet",

    HIT_AREA: "HitArea",
};

/**
 * XML has attributes ContainerElementAttributes, BaseElementAttributes
 * @type {{STYLE: string}}
 */
export const ContainerElementAttributes = {
    STYLE: "Style",
    MULTIPLY: "Multiply",
    CHILDS: {
        X: "ChildsX",
        Y: "ChildsY",
        SCALE: "ChildsScale",
        ALPHA: "ChildsAlpha",
        ROTATION: "ChildsRotation",

        ALPHA_TWEEN: "ChildsAlphaTween",
        ALPHA_TWEEN_EASE: "ChildsAlphaTweenEase",
        ALPHA_TWEEN_DELAY: "ChildsAlphaTweenDelay",
        ALPHA_TWEEN_DURATION: "ChildsAlphaTweenDuration",

        ROTATION_TWEEN: "ChildsRotationTween",
        ROTATION_TWEEN_EASE: "ChildsRotationTweenEase",
        ROTATION_TWEEN_DELAY: "ChildsRotationTweenDelay",
        ROTATION_TWEEN_DURATION: "ChildsRotationTweenDuration",

        SCALE_TWEEN: "ChildsScaleTween",
        SCALE_TWEEN_X: "ChildsScaleTweenX",
        SCALE_TWEEN_Y: "ChildsScaleTweenY",
        SCALE_TWEEN_EASE: "ChildsScaleTweenEase",
        SCALE_TWEEN_DELAY: "ChildsScaleTweenDelay",
        SCALE_TWEEN_DURATION: "ChildsScaleTweenDuration",

        POSITION_TWEEN_POSITION: "ChildsPositionTweenPosition",
        POSITION_TWEEN_X: "ChildsPositionTweenX",
        POSITION_TWEEN_Y: "ChildsPositionTweenY",
        POSITION_TWEEN_EASE: "ChildsPositionTweenEase",
        POSITION_TWEEN_DELAY: "ChildsPositionTweenDelay",
        POSITION_TWEEN_DURATION: "ChildsPositionTweenDuration",
    },
};

export const ImageElementAttributes = {
    /*
     * Alpha (transparency) for this display object. 0 is fully transparent, 1 is fully opaque.
     * @property Alpha
     * @type Number
     * @default 1
     */
    ALPHA: "Alpha",
    ALPHA_TWEEN: "AlphaTween",
    ALPHA_TWEEN_EASE: "AlphaTweenEase",
    ALPHA_TWEEN_DELAY: "AlphaTweenDelay",
    ALPHA_TWEEN_DURATION: "AlphaTweenDuration",
    ALPHA_TWEEN_LOOP: "AlphaTweenLoop",
    ANCHOR: "Anchor",
    /**
     * Target animation from where is specific frame selected. Attribute Frame="?" needed.
     */
    FROM_FRAME: "FromFrame",
    /*
     * A ImageElement instance that defines a mask for this display object.
     * The shape's transformation will be applied relative to the display object's parent coordinates (as if it were a child of the parent).
     * @property BitmapMask
     * @type string
     * @default null
     */
    BITMAP_MASK: "BitmapMask",
    BITMAP_MASK_SHOW: "BitmapMaskShow",
    BITMAP_MASK_X: "BitmapMaskX",
    BITMAP_MASK_Y: "BitmapMaskY",
    /*
     * Blend mode for animation.
     * Bleding modes:
     *   0 = NORMAL;
     *   1 = ADD;
     *   2 = MULTIPLY;
     *   3 = SCREEN;
     *   4 = OVERLAY;
     *   5 = DARKEN;
     *   6 = LIGHTEN;
     *   7 = COLOR_DODGE;
     *   8 = COLOR_BURN;
     *   9 = HARD_LIGHT;
     *   10 = SOFT_LIGHT;
     *   11 = DIFFERENCE;
     *   12 = EXCLUSION;
     *   13 = HUE;
     *   14 = SATURATION;
     *   15 = COLOR;
     *   16 = LUMINOSITY;
     * @property BlendMode
     * @type number
     * @default null
     */
    BLEND_MODE: "BlendMode",
    /*
     * If true cache image as bitmap.
     * @property Cache
     * @type boolean
     * @default null
     */
    CACHE: "Cache",
    DEBUG_INFO: "DebugInfo",
    DEBUG: "Debug",

    FRAME: "Frame",
    /*
     * Reference to image from resources.
     * @property Image
     * @type string
     * @default 0
     */
    IMAGE: "Image",
    /*
     * A ShapeElement instance that defines a vector mask (clipping path) for this display object.
     * The shape's transformation will be applied relative to the display object's parent coordinates (as if it were a child of the parent).
     * @property Mask
     * @type ShapeElement
     * @default null
     */
    MASK: "Mask",
    /*
     * X pivot for container. Used mainly for scale tweening and rotation.
     * @property PivotX
     * @type number
     * @default null
     */
    PIVOT_X: "PivotX",
    /*
     * Y pivot for container. Used mainly for scale tweening and rotation.
     * @property PivotY
     * @type number
     * @default null
     */
    PIVOT_Y: "PivotY",
    POSITION_TWEEN_X: "PositionTweenX",
    POSITION_TWEEN_Y: "PositionTweenY",
    POSITION_TWEEN_EASE: "PositionTweenEase",
    POSITION_TWEEN_DURATION: "PositionTweenDuration",
    POSITION_TWEEN_DELAY: "PositionTweenDelay",
    POSITION_TWEEN_POSITION: "PositionTweenPosition",
    POSITION_TWEEN_POSITION_VALUE: {
        ABSOLUTE: "absolute",
        RELATIVE: "relative",
    },
    POSITION_TWEEN_LOOP: "PositionTweenLoop",
    /*
     * Rotation in degrees for this display object.
     * @property Rotation
     * @type Number
     * @default 0
     */
    ROTATION: "Rotation",
    ROTATION_TWEEN: "RotationTween",
    ROTATION_TWEEN_EASE: "RotationTweenEase",
    ROTATION_TWEEN_DURATION: "RotationTweenDuration",
    ROTATION_TWEEN_DELAY: "RotationTweenDelay",
    ROTATION_TWEEN_LOOP: "RotationTweenLoop",
    /**
     *
     */
    SCALE_TWEEN: "ScaleTween",
    SCALE_TWEEN_X: "ScaleTweenX",
    SCALE_TWEEN_Y: "ScaleTweenY",
    SCALE_TWEEN_EASE: "ScaleTweenEase",
    SCALE_TWEEN_DELAY: "ScaleTweenDelay",
    SCALE_TWEEN_DURATION: "ScaleTweenDuration",
    SCALE_TWEEN_LOOP: "ScaleTweenLoop",
    /*
     * The factor to stretch this display object horizontally and vertically. Use this instead of using ScaleX and ScaleY with same value.
     * @property Scale
     * @type Number
     * @default 1
     */
    SCALE: "Scale",
    SCALE_X: "ScaleX",
    /*
     * The factor to stretch this display object vertically.
     * For example, setting scaleY to 0.5 will stretch the display object to half its nominal height. To vertically flip an object, set the scale to a negative number.
     * @property ScaleY
     * @type Number
     * @default 1
     */
    SCALE_Y: "ScaleY",
    /*
     * Random rotation in degrees for this display object.
     * @property StartRotation
     * @type Number
     * @default null
     */
    START_ROTATION: "StartRotation",
    /*
     * Tint of image. Hex color which colorize movie clip.
     * @property Tint
     * @type string
     * @default null
     */
    TINT: "Tint",
    TWEEN_ENGINE: "TweenEngine",
    VISIBLE: "Visible",
    /*
     * X (horizontal) position of the display object, relative to its parent.
     * @property X
     * @type Number
     * @default 0
     */
    X: "X",
    /*
     * Y (vertical) position of the display object, relative to its parent.
     * @property Y
     * @type Number
     * @default 0
     */
    Y: "Y",
};

/**
 *
 * @type {{DURATION: string; ROUND: string; FORCE: string}}
 */
export const ImageTextCounterElementAttributes = {
    /*
     * Duration in frames. This property set how long will counter running in frames.
     * @property Duration
     * @type number
     * @default 0
     */
    DURATION: "Duration",
    /*
     * Round will show numbers without decimal places.
     * @property Round
     * @type boolean
     * @default false
     */
    ROUND: "Round",
    /*
     * Force counting to end.
     * @property Force
     * @type boolean
     * @default true
     */
    FORCE: "Force",
    VALUE: "Value",
};

/**
 *
 * @type {{DURATION: string; ROUND: string; FORCE: string}}
 */
export const ImageTextElementAttributes = {
    /*
     * The text to display.
     * @property Text
     * @type string
     * @default null
     */
    TEXT: "Text",
    /*
     * Bitmap font for text.
     * @property Font
     * @type string
     * @default null
     */
    FONT: "Font",
    /*
     * The horizontal text alignment. Any of "left", "right", and "center".
     * For detailed information view the whatwg spec. Default is "left".
     * @property TextAlign
     * @type string
     * @default left
     */
    TEXT_ALIGN: "TextAlign",
    TEXT_ALIGN_VALUE: {
        CENTER: "center",
        RIGHT: "right",
    },
    /*
     * The vertical alignment point on the text.
     * Any of "top", "middle" or "bottom". Default is "top".
     * @property TextBaseline
     * @type string
     * @default top
     */
    TEXT_BASELINE: "TextBaseline",
    TEXT_BASELINE_VALUE: {
        MIDDLE: "middle",
        BOTTOM: "bottom",
    },
    /*
     * Alpha (transparency) for this display object. 0 is fully transparent, 1 is fully opaque.
     * @property Alpha
     * @type Number
     * @default 1
     */
    ALPHA: "Alpha",
    /*
     * Rotation in degrees for this display object.
     * @property Rotation
     * @type Number
     * @default 0
     */
    ROTATION: "Rotation",
    /*
     * Random rotation in degrees for this display object.
     * @property StartRotation
     * @type Number
     * @default null
     */
    START_ROTATION: "StartRotation",
    /*
     * The factor to stretch this display object horizontally.
     * For example, setting scaleX to 2 will stretch the display object to twice its nominal width. To horizontally flip an object, set the scale to a negative number.
     * @property ScaleX
     * @type Number
     * @default 1
     */
    SCALE_X: "ScaleX",
    /*
     * The factor to stretch this display object vertically.
     * For example, setting scaleY to 0.5 will stretch the display object to half its nominal height. To vertically flip an object, set the scale to a negative number.
     * @property ScaleY
     * @type Number
     * @default 1
     */
    SCALE_Y: "ScaleY",
    /*
     * The factor to stretch this display object horizontally and vertically. Use this instead of using ScaleX and ScaleY with same value.
     * @property Scale
     * @type Number
     * @default 1
     */
    SCALE: "Scale",
    /*
     * X (horizontal) position of the display object, relative to its parent.
     * @property X
     * @type Number
     * @default 0
     */
    X: "X",
    /*
     * Y (vertical) position of the display object, relative to its parent.
     * @property Y
     * @type Number
     * @default 0
     */
    Y: "Y",
    /*
     * X pivot for container. Used mainly for scale tweening and rotation.
     * @property PivotX
     * @type number
     * @default null
     */
    PIVOT_X: "PivotX",
    /*
     * Y pivot for container. Used mainly for scale tweening and rotation.
     * @property PivotY
     * @type number
     * @default null
     */
    PIVOT_Y: "PivotY",
    /*
     * Indicates whether this display object should be rendered to the canvas and included when running the Stage Stage/getObjectsUnderPoint method.
     * @property Visible
     * @type Boolean
     * @default true
     */
    VISIBLE: "Visible",
    /*
     * A ShapeElement instance that defines a vector mask (clipping path) for this display object.
     * The shape's transformation will be applied relative to the display object's parent coordinates (as if it were a child of the parent).
     * @property Mask
     * @type ShapeElement
     * @default null
     */
};

export const LinkedElementAttributes = {
    LINKED_TO_ID: "LinkedToId",
};

/**
 * Extended by BaseElementAttributes
 * @type {{TINT: string; BLEND_MODE: string; STYLE: string; STYLE2: string; START: string; EMIT: string; STOPPING: string; STOP: string; ALPHA_TWEEN_EASE: string; ALPHA_TWEEN_DELAY: string; ALPHA_TWEEN_DURATION: string; ROTATION_TWEEN: string; ROTATION_TWEEN_EASE: string; ROTATION_TWEEN_DURATION: string; ROTATION_TWEEN_DELAY: string; SCALE_TWEEN: string; SCALE_TWEEN_EASE: string; SCALE_TWEEN_DURATION: string; POSITION_TWEEN_X: string; POSITION_TWEEN_Y: string; POSITION_TWEEN_EASE: string; POSITION_TWEEN_DURATION: string; POSITION_TWEEN_POSITION: string; POSITION_TWEEN_POSITION_VALUE: {ABSOLUTE: string; RELATIVE: string}}}
 */
export const ParticleElementAttributes = {
    /*
     * Blend mode for animation.
     * Bleding modes:
     *   0 = NORMAL;
     *   1 = ADD;
     *   2 = MULTIPLY;
     *   3 = SCREEN;
     *   4 = OVERLAY;
     *   5 = DARKEN;
     *   6 = LIGHTEN;
     *   7 = COLOR_DODGE;
     *   8 = COLOR_BURN;
     *   9 = HARD_LIGHT;
     *   10 = SOFT_LIGHT;
     *   11 = DIFFERENCE;
     *   12 = EXCLUSION;
     *   13 = HUE;
     *   14 = SATURATION;
     *   15 = COLOR;
     *   16 = LUMINOSITY;
     * @property BlendMode
     * @type number
     * @default null
     */
    BLEND_MODE: "BlendMode",
    SPEED: "Speed",
    EMIT_ONCE: "EmitOnce",
    START_TYPE: "StartType",
    // Tweens used in BaseElementParticle
    ALPHA_TWEEN: "AlphaTween",
    ALPHA_TWEEN_EASE: "AlphaTweenEase",
    ALPHA_TWEEN_DELAY: "AlphaTweenDelay",
    ALPHA_TWEEN_DURATION: "AlphaTweenDuration",

    POSITION_TWEEN_X: "PositionTweenX",
    POSITION_TWEEN_Y: "PositionTweenY",
    POSITION_TWEEN_EASE: "PositionTweenEase",
    POSITION_TWEEN_DURATION: "PositionTweenDuration",
    POSITION_TWEEN_POSITION: "PositionTweenPosition",
    POSITION_TWEEN_POSITION_VALUE: {
        ABSOLUTE: "absolute",
        RELATIVE: "relative",
    },

    ROTATION_TWEEN: "RotationTween",
    ROTATION_TWEEN_EASE: "RotationTweenEase",
    ROTATION_TWEEN_DURATION: "RotationTweenDuration",
    ROTATION_TWEEN_DELAY: "RotationTweenDelay",

    SCALE_TWEEN: "ScaleTween",
    SCALE_TWEEN_EASE: "ScaleTweenEase",
    SCALE_TWEEN_DURATION: "ScaleTweenDuration",
    /*
     * Style define animation. You can use one style on more than one place.
     * Style for ParticleElement has to be instance of ParticleStyle.
     * @property Style
     * @type string
     * @default null
     */
    STYLE: "Style",
    STYLE2: "Style2",
    EXTEND_STYLE: "ExtendStyle",
    START: "Start",
    STOPPING: "Stopping",
    STOP: "Stop",
    /*
     * Tint of animation. Hex color which colorize movie clip.
     * @property Tint
     * @type string
     * @default null
     */
    TINT: "Tint",
    ATTR_COUNT: "Count",
    ATTR_TIME: "Time",
    ATTR_MIN: "Min",
    ATTR_MAX: "Max",
    ATTR_FROM_FRAME: "FromFrame",
    ATTR_IMAGES: "Images",
    ATTR_TYPE: "Type",
    ATTR_X: "X",
    ATTR_Y: "Y",
    ATTR_RANDOM_X: "RandomX",
    ATTR_RANDOM_Y: "RandomY",
    ATTR_WIDTH: "Width",
    ATTR_HEIGHT: "Height",
    ATTR_VALUES: "Values",
    ATTR_VALUE: "Value",
};

/**
 * XML has attributes SwitchElementAttributes, BaseElementAttributes
 * @type {{STYLE: string; CASE: string}}
 */
export const SwitchElementAttributes = {
    /*
     * Property case is used for set element to some case
     * @property Case
     * @type string
     * @default null
     */
    CASE: "Case",
    DEBUG_INFO: "DebugInfo",
    /*
     * Style holds elements as ContainerElement and also holds cases as xml nodes.
     * Style for switch element has to be instance of SwitchStyle.
     * @property Style
     * @type string
     * @default null
     */
    STYLE: "Style",
};

export const SwitchStyleAttributes = {
    CASE: "Case",
};

export const SwitchStyleCaseAttributes = {
    NEXT_CASE: "NextCase",
    TIMER: "Timer",
    HANDLER: "Handler",
};

/**
 *
 * @type {{DURATION: string; ROUND: string; FORCE: string}}
 */
export const TextElementAttributes = {
    /**
     * The text to display.
     * @property Text
     * @type string
     * @default null
     */
    TEXT: "Text",
    /*
     * Bitmap font for text.
     * @property Font
     * @type string
     * @default null
     */
    FONT: "Font",
    /**
     * The font size (as a number it converts to px, but as a string, equivalents are '26px','20pt','160%' or '1.6em')
     */
    SIZE: "Size",
    /*
     * The horizontal text alignment. Any of "left", "right", and "center".
     * For detailed information view the whatwg spec. Default is "left".
     * @property TextAlign
     * @type string
     * @default left
     */
    TEXT_ALIGN: "TextAlign",
    TEXT_ALIGN_VALUE: {
        LEFT: "left",
        CENTER: "center",
        RIGHT: "right",
    },
    /*
     * The vertical alignment point on the text.
     * Any of "top", "middle" or "bottom". Default is "top".
     * @property TextBaseline
     * @type string
     * @default top
     */
    TEXT_BASELINE: "TextBaseline",
    TEXT_BASELINE_VALUE: {
        MIDDLE: "middle",
        BOTTOM: "bottom",
    },
    /*
     * Alpha (transparency) for this display object. 0 is fully transparent, 1 is fully opaque.
     * @property Alpha
     * @type Number
     * @default 1
     */
    ALPHA: "Alpha",
    /*
     * Rotation in degrees for this display object.
     * @property Rotation
     * @type Number
     * @default 0
     */
    ROTATION: "Rotation",
    /*
     * Random rotation in degrees for this display object.
     * @property StartRotation
     * @type Number
     * @default null
     */
    START_ROTATION: "StartRotation",
    /*
     * The factor to stretch this display object horizontally.
     * For example, setting scaleX to 2 will stretch the display object to twice its nominal width. To horizontally flip an object, set the scale to a negative number.
     * @property ScaleX
     * @type Number
     * @default 1
     */
    SCALE_X: "ScaleX",
    /*
     * The factor to stretch this display object vertically.
     * For example, setting scaleY to 0.5 will stretch the display object to half its nominal height. To vertically flip an object, set the scale to a negative number.
     * @property ScaleY
     * @type Number
     * @default 1
     */
    SCALE_Y: "ScaleY",
    /*
     * The factor to stretch this display object horizontally and vertically. Use this instead of using ScaleX and ScaleY with same value.
     * @property Scale
     * @type Number
     * @default 1
     */
    SCALE: "Scale",
    /*
     * X (horizontal) position of the display object, relative to its parent.
     * @property X
     * @type Number
     * @default 0
     */
    X: "X",
    /*
     * Y (vertical) position of the display object, relative to its parent.
     * @property Y
     * @type Number
     * @default 0
     */
    Y: "Y",
    /*
     * X pivot for container. Used mainly for scale tweening and rotation.
     * @property PivotX
     * @type number
     * @default null
     */
    PIVOT_X: "PivotX",
    /*
     * Y pivot for container. Used mainly for scale tweening and rotation.
     * @property PivotY
     * @type number
     * @default null
     */
    PIVOT_Y: "PivotY",
    /*
     * Indicates whether this display object should be rendered to the canvas and included when running the Stage Stage/getObjectsUnderPoint method.
     * @property Visible
     * @type Boolean
     * @default true
     */
    VISIBLE: "Visible",
    /*
     * Tint of image. Hex color which colorize movie clip.
     * @property Tint
     * @type string
     * @default null
     */
    TINT: "Tint",
};

/**
 * XML has attributes TimelineElementAttributes, BaseElementAttributes
 * @type {{LOOP: string; TINT: string; STYLE: string; CLONE: string; SOUNDS: string; PLAY: string}}
 */
export const TimelineElementAttributes = {
    /*
     * Indicates whenever this element should loop when it reaches the end of its timeline.
     * @property Loop
     * @type boolean
     * @default false
     */
    LOOP: "Loop",
    /*
     * Tint of animation. Hex color which colorize movie clip.
     * @property Tint
     * @type string
     * @default null
     */
    TINT: "Tint",
    /*
     * Style holds elements as ContainerElement and also holds frames as xml nodes.
     * Style for TimelineElement has to be instance of TimelineStyle.
     * @property Style
     * @type string
     * @default null
     */
    STYLE: "Style",
    CLONE: "Clone",
    /*
     * Playlist id for machine.
     * @property Sounds
     * @type string
     * @default null
     */
    SOUNDS: "Sounds",
    /*
     * Start playing animation.
     * @property Play
     * @type boolean
     */
    PLAY: "Play",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/Components/ComponentAttributes.ts
 **/